/**
 * MSE Tier Preview (Farbgel)
 * Zeigt aktuelle und nächste Staffelpreise anhand der Farbgel-Menge im Warenkorb.
 */

const MSE_DEFAULT_FARBGEL_TIERS = [
  { qty: 1, price_cents: 356 },
  { qty: 100, price_cents: 344 },
  { qty: 200, price_cents: 332 },
  { qty: 300, price_cents: 308 },
  { qty: 500, price_cents: 275 },
  { qty: 1000, price_cents: 270 }
];

async function mseFetchCart() {
  const res = await fetch('/cart.js', { credentials: 'same-origin' });
  if (!res.ok) throw new Error('Cart fetch failed');
  return await res.json();
}

function mseNormalize(s) {
  return String(s || '').toLowerCase().trim();
}

function mseIsFarbgelItem(item) {
  const pt = mseNormalize(item.product_type);
  const title = mseNormalize(item.product_title || item.title);
  const handle = mseNormalize(item.handle);
  const url = mseNormalize(item.url);

  return (
    /farb\s*gel/.test(title) ||
    /farbgel/.test(title) ||
    /color\s*gel/.test(title) ||
    /colour\s*gel/.test(title) ||
    /farb/.test(pt) ||
    /color\s*gel/.test(pt) ||
    /colour\s*gel/.test(pt) ||
    /farbgel/.test(handle) ||
    /color-gel/.test(handle) ||
    /colour-gel/.test(handle) ||
    /farbgel/.test(url) ||
    /color-gel/.test(url) ||
    /colour-gel/.test(url)
  );
}

function mseCountFarbgel(cart) {
  let qty = 0;

  for (const item of cart.items || []) {
    if (mseIsFarbgelItem(item)) {
      qty += Number(item.quantity || 0);
    }
  }

  return qty;
}

/*
 * MSE_TIER_VIRTUAL_PREORDER_V109_20260917
 *
 * Im Preorder-Testmodus wird die Staffel aus den
 * servervalidierten virtuellen Wunschmengen berechnet.
 */
function mseVirtualPreorderQuantity() {
  try {
    if (
      sessionStorage.getItem(
        'mse_preorder_virtual_v108_test'
      ) !== '1'
    ) {
      return null;
    }

    const stored = JSON.parse(
      sessionStorage.getItem(
        'mse_preorder_virtual_v108_items'
      ) || '{}'
    );

    if (
      !stored
      || typeof stored !== 'object'
      || Array.isArray(stored)
    ) {
      return 0;
    }

    let total = 0;

    for (const entry of Object.values(stored)) {
      const sku =
        String(entry?.sku || '').trim();

      const ordered =
        Number.parseInt(
          entry?.preview?.summary?.ordered_quantity,
          10
        );

      const validSku =
        /^150(?:1[0-9]{3}|5[0-9]{3}|5P[0-9]{2}|5S[0-9]{2})$/i
          .test(sku);

      const validated =
        entry?.preview?.ok === true
        && entry?.preview?.preview_stateless === true
        && entry?.preview?.checkout_enabled === false;

      if (
        validSku
        && validated
        && Number.isInteger(ordered)
        && ordered > 0
      ) {
        total += ordered;
      }
    }

    return total;

  } catch (error) {
    console.warn(
      'MSE_TIER_VIRTUAL_PREORDER_V109_20260917',
      error
    );

    return 0;
  }
}

function mseTierLocale() {
  const raw = String(document.documentElement.lang || 'de').toLowerCase();
  const code = raw.split('-')[0];
  return ['de', 'en', 'fr', 'vi'].includes(code) ? code : 'de';
}

const MSE_TIER_I18N = {
  de: {
    currentTier: '✅ Aktuelle Staffel:',
    from: 'ab',
    unitOne: 'Stück',
    unitMany: 'Stück',
    priceFrom: 'Dein Preis ab {qty} {unit}:',
    net: 'netto',
    reached: 'Diese Staffel ist aktuell erreicht.',
    missing: 'Nur noch {missing} Stück bis zur nächsten Staffel',
    highest: 'Du hast bereits die höchste Staffel erreicht.',
    virtualHint: 'Hinweis: Wird anhand deiner Farbgel-Wunschmenge berechnet.'
  },
  en: {
    currentTier: '✅ Current tier:',
    from: 'from',
    unitOne: 'pc',
    unitMany: 'pcs',
    priceFrom: 'Your price from {qty} {unit}:',
    net: 'net',
    reached: 'You’ve reached this tier.',
    missing: 'Only {missing} more to reach the next tier',
    highest: 'You’ve already reached the best price tier.',
    virtualHint: 'Note: Calculated from your requested color-gel quantity.'
  },
  fr: {
    currentTier: '✅ Tarif actuel :',
    from: 'à partir de',
    unitOne: 'pièce',
    unitMany: 'pièces',
    priceFrom: 'Votre prix dès {qty} {unit} :',
    net: 'HT',
    reached: 'Ce tarif est déjà atteint.',
    missing: 'Plus que {missing} pour atteindre le tarif suivant',
    highest: 'Vous avez déjà atteint le meilleur tarif.',
    virtualHint: 'Remarque : calculé selon la quantité de gels couleur souhaitée.'
  },
  vi: {
    currentTier: '✅ Mức giá hiện tại:',
    from: 'từ',
    unitOne: 'sản phẩm',
    unitMany: 'sản phẩm',
    priceFrom: 'Giá từ {qty} {unit}:',
    net: 'chưa VAT',
    reached: 'Bạn đã đạt mức giá này.',
    missing: 'Còn {missing} sản phẩm để đạt mức giá tiếp theo',
    highest: 'Bạn đã đạt mức giá tốt nhất.',
    virtualHint: 'Lưu ý: Giá được tính theo số lượng gel màu bạn muốn đặt.'
  }
};

function mseTierText() {
  return MSE_TIER_I18N[mseTierLocale()] || MSE_TIER_I18N.de;
}

function mseTierInterpolate(text, vars) {
  return String(text).replace(/\{(\w+)\}/g, (_, key) => String(vars[key] ?? ''));
}

function mseFormatMoneyDE(cents) {
  const locales = {
    de: 'de-DE',
    en: 'en-GB',
    fr: 'fr-FR',
    vi: 'vi-VN'
  };

  return (Number(cents || 0) / 100).toLocaleString(
    locales[mseTierLocale()] || 'de-DE',
    {
      style: 'currency',
      currency: 'EUR'
    }
  );
}

function mseGrossToNetCents(grossCents) {
  return Math.max(0, Math.round((Number(grossCents || 0) * 100) / 119));
}

function mseGetTiers(el) {
  let tiers = [];

  try {
    tiers = JSON.parse(el.getAttribute('data-tiers') || '[]');
  } catch (e) {
    tiers = [];
  }

  if (!Array.isArray(tiers) || !tiers.length) {
    tiers = MSE_DEFAULT_FARBGEL_TIERS;
  }

  return tiers
    .map(t => ({
      qty: Number(t.qty || 0),
      price_cents: Number(t.price_cents || 0),
      net_cents: Number(t.net_cents || 0)
    }))
    .filter(t => t.qty > 0 && t.price_cents > 0)
    .sort((a, b) => a.qty - b.qty);
}

function mseCurrentTier(totalQty, tiers) {
  return tiers.filter(t => totalQty >= t.qty).pop() || null;
}

function mseNextTiers(totalQty, tiers) {
  return tiers.filter(t => totalQty < t.qty).slice(0, 2);
}

function mseTierLine(tier, totalQty, isActive) {
  const qty = tier.qty;
  const grossCents = tier.price_cents;
  const netCents = tier.net_cents > 0
    ? tier.net_cents
    : mseGrossToNetCents(grossCents);
  const missing = Math.max(0, qty - totalQty);
  const copy = mseTierText();
  const unit = qty === 1 ? copy.unitOne : copy.unitMany;
  const headline = `${isActive ? copy.currentTier : copy.from} ${qty} ${unit}`;
  const priceLabel = mseTierInterpolate(copy.priceFrom, { qty, unit });

  return `
    <div style="
      padding:10px 12px;
      border-radius:12px;
      border:1px solid ${isActive ? 'rgba(22,163,74,.35)' : 'rgba(0,0,0,.08)'};
      background:${isActive ? 'rgba(22,163,74,.08)' : 'rgba(255,255,255,.55)'};
    ">
      <div style="font-weight:900;">
        ${headline}
      </div>

      <div style="opacity:.88; font-size:13px; margin-top:2px;">
        ${priceLabel}
        <strong>${mseFormatMoneyDE(grossCents)}</strong>
        <span style="opacity:.9;">(${copy.net} ${mseFormatMoneyDE(netCents)})</span>
      </div>

      ${
        isActive
          ? `<div style="opacity:.78; font-size:12px; margin-top:4px;">${copy.reached}</div>`
          : `<div style="opacity:.75; font-size:12px; margin-top:4px;">${mseTierInterpolate(copy.missing, { missing })}</div>`
      }
    </div>
  `;
}

function mseRenderTierPreview(el, totalQty, tiers) {
  const rows = el.querySelector('.mse-tier-preview__rows');
  if (!rows) return;

  const current = mseCurrentTier(totalQty, tiers);
  const next = mseNextTiers(totalQty, tiers);

  let html = '';

  if (current) {
    html += mseTierLine(current, totalQty, true);
  }

  if (next.length) {
    html += next.map(t => mseTierLine(t, totalQty, false)).join('');
  }

  if (!html) {
    html = `<div style="font-weight:700;">${mseTierText().highest}</div>`;
  }

  rows.innerHTML = html;

  /*
   * MSE_TIER_VIRTUAL_NOTE_V110_20260917
   * Im Preorder-Testmodus klarstellen, dass die
   * virtuelle Wunschmenge die Staffel bestimmt.
   */
  const hint =
    rows.nextElementSibling;

  if (hint) {
    if (!hint.dataset.mseV110OriginalText) {
      hint.dataset.mseV110OriginalText =
        hint.textContent.trim();
    }

    const virtualQty =
      mseVirtualPreorderQuantity();

    if (virtualQty !== null) {
      hint.textContent =
        mseTierText().virtualHint;
    } else {
      hint.textContent =
        hint.dataset.mseV110OriginalText;
    }
  }
}

async function mseInitTierPreview() {
  const els = document.querySelectorAll('.mse-tier-preview');
  if (!els.length) return;

  const virtualQty =
    mseVirtualPreorderQuantity();

  let totalQty = 0;

  if (virtualQty !== null) {
    totalQty = virtualQty;
  } else {
    let cart;

    try {
      cart = await mseFetchCart();
    } catch (e) {
      cart = { items: [] };
    }

    totalQty =
      mseCountFarbgel(cart);
  }

  els.forEach(el => {
    const tiers = mseGetTiers(el);

    mseRenderTierPreview(
      el,
      totalQty,
      tiers
    );
  });
}

document.addEventListener('DOMContentLoaded', mseInitTierPreview);

document.addEventListener(
  'mse:preorder-virtual:updated',
  mseInitTierPreview
);

document.addEventListener('cart:updated', mseInitTierPreview);
document.addEventListener('cart:refresh', mseInitTierPreview);
document.addEventListener('cart:change', mseInitTierPreview);

document.addEventListener('click', function () {
  setTimeout(mseInitTierPreview, 500);
});

/* MSE_TIER_VARIANT_VISIBILITY_1505001_V1_20260914 */
(() => {
  const mse001Path =
    '/products/001-effekt-farbgel-color-gel-5ml-dezent-schimmernder-nude-ton';

  function isMse001Page() {
    return window.location.pathname === mse001Path ||
           window.location.pathname === mse001Path + '/';
  }

  function currentSku() {
    return (
      document.querySelector('.product-sku__value')?.textContent || ''
    ).trim();
  }

  function applyTierVisibility(sku) {
    if (!isMse001Page()) return;

    const show = String(sku || '').trim() === '1505001';

    document.querySelectorAll('.mse-tier-preview').forEach((el) => {
      el.hidden = !show;
    });
  }

  function init() {
    applyTierVisibility(currentSku());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('on:variant:change', (evt) => {
    const sku =
      evt.detail?.variant?.sku ||
      currentSku();

    applyTierVisibility(sku);
  });

  window.addEventListener('pageshow', init);
})();
/* /MSE_TIER_VARIANT_VISIBILITY_1505001_V1_20260914 */


/* MSE_TIER_ALL_1505_1501_JS_V1_20260914 */
(() => {
  function isFarbgel5mlSku(sku) {
    sku = String(sku || '').trim();
    return sku.startsWith('1505') || sku.startsWith('1501');
  }

  function currentSku() {
    return (
      document.querySelector('.product-sku__value')?.textContent || ''
    ).trim();
  }

  function apply(sku) {
    const show = isFarbgel5mlSku(sku);

    document.querySelectorAll('.mse-tier-preview').forEach((el) => {
      el.hidden = !show;
    });
  }

  function init() {
    apply(currentSku());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once:true });
  } else {
    init();
  }

  document.addEventListener('on:variant:change', (evt) => {
    apply(evt.detail?.variant?.sku || currentSku());
  });

  window.addEventListener('pageshow', init);
})();
/* /MSE_TIER_ALL_1505_1501_JS_V1_20260914 */
